import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import { Country, State } from "country-state-city";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useAddCityMutation,
  useEditCityMutation,
  useGetCityDetailsQuery,
} from "store/api/cities.api";
import Formatmessage from "services/Formatmessage";
import useLogout from "services/Handlelogout";
import schema from "validationSchema/addcity";
import { FadeLoader } from "react-spinners";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";

const labelSx = {
  fontSize: "12px",
  color: "#3D484E",
  fontWeight: 500,
};

const inputFieldSx = {
  width: "100% !important",
  py: "14px",
  backgroundColor: "#F9F9F9 !important",
  border: "1px solid #BCC8CE !important",
  borderRadius: "12px !important",
  color: "#1A1C1C",
  fontSize: "14px",
  "& .MuiInputBase-input": {
    color: "#1A1C1C !important",
    fontSize: "14px !important",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#666666 !important",
    fontSize: "14px !important",
    opacity: 1,
  },
};

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#F9F9F9",
    minHeight: "48px",
    py: 0.25,
    color: "#1A1C1C",
    fontSize: "14px",
    "& fieldset": { borderColor: "#BCC8CE" },
    "&:hover fieldset": { borderColor: "#BCC8CE" },
    "&.Mui-focused fieldset": { borderColor: "#006780" },
    "& input": {
      color: "#1A1C1C",
      fontSize: "14px",
      "&::placeholder": {
        color: "#666666",
        opacity: 1,
        fontSize: "14px",
      },
    },
  },
};

function AddCity() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const backPath = location.state?.from || "/cities";
  const logout = useLogout();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [addCity] = useAddCityMutation();
  const [editCity] = useEditCityMutation();

  const [form, setForm] = useState({
    name: "",
    state: "",
    country: "",
    country_abbr: "",
    is_active: true,
  });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : []),
    [selectedCountry]
  );

  const {
    data: cityDetailsData,
    isLoading: cityDetailsLoading,
    isFetching,
  } = useGetCityDetailsQuery(id ? { city_id: id } : skipToken);

  useEffect(() => {
    if (!id || !cityDetailsData) return;

    if (cityDetailsData?.status === 1) {
      const city = cityDetailsData?.data || {};
      setForm({
        name: city?.city || "",
        state: city?.state || "",
        country: city?.country || "",
        country_abbr: city?.country_abbr || "",
        is_active: city?.is_active ?? true,
      });

      const matchedCountry =
        countries.find(
          (c) =>
            c.isoCode === city?.country_abbr ||
            c.name?.toLowerCase() === city?.country?.toLowerCase()
        ) || null;

      setSelectedCountry(matchedCountry);

      if (matchedCountry) {
        const countryStates = State.getStatesOfCountry(matchedCountry.isoCode);
        const matchedState =
          countryStates.find((s) => s.name?.toLowerCase() === city?.state?.toLowerCase()) || null;
        setSelectedState(matchedState);
      } else {
        setSelectedState(null);
      }
    } else if (cityDetailsData?.status == 0) {
      toast.error(Formatmessage(cityDetailsData?.message));
    } else if (cityDetailsData?.status == 10) {
      logout();
    }
  }, [id, cityDetailsData, countries]);

  const handleCountryChange = (_, value) => {
    setSelectedCountry(value);
    setSelectedState(null);
    setForm((prev) => ({
      ...prev,
      country: value?.name || "",
      country_abbr: value?.isoCode || "",
      state: "",
    }));
    setErrors((prev) => ({ ...prev, country: "", country_abbr: "", state: "" }));
  };

  const handleStateChange = (_, value) => {
    setSelectedState(value);
    setForm((prev) => ({
      ...prev,
      state: value?.name || "",
    }));
    setErrors((prev) => ({ ...prev, state: "" }));
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validateForm = () => {
    const result = schema.safeParse({
      name: form.name,
      state: form.state,
      country: form.country,
      country_abbr: form.country_abbr,
      is_active: form.is_active,
    });

    if (!result.success) {
      const fieldErrors = {};
      const zodErrors = result.error?.issues || [];
      zodErrors.forEach((issue) => {
        const key = issue.path?.[0];
        if (key && !fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      });
      setErrors(fieldErrors);
      const firstMessage = zodErrors[0]?.message;
      if (firstMessage) toast.error(firstMessage);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    let payload = {
      city: form.name.trim(),
      state: form.state.trim(),
      country: form.country.trim(),
      country_abbr: form.country_abbr.trim(),
      is_active: form.is_active,
    };

    if (id) {
      payload = { ...payload, city_id: id };
    }

    try {
      const response = id
        ? await editCity(payload).unwrap()
        : await addCity(payload).unwrap();

      if (response?.status == 1) {
        toast.success(Formatmessage(response?.message));
        navigate(backPath);
      } else if (response?.status == 0) {
        toast.error(Formatmessage(response?.message));
      } else if (response?.status == 10) {
        logout();
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Server error. please try again in a few minutes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox
        sx={{
          pr: { xs: 1, sm: 2, md: 3 },
          pl: { xs: 1, sm: 2, md: 2 },
          top: { xs: "55%", sm: "40%" },
        }}
        position="absolute"
        width="100%"
      >
        {id && (cityDetailsLoading || isFetching) ? (
          <SoftBox
            sx={{
              height: "calc(70vh)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FadeLoader color="#3DC2EB" />
          </SoftBox>
        ) : (
          <SoftBox width="100%" display="flex" sx={{ pb: 4 }}>
            <SoftBox
              width="100%"
              display="flex"
              flexDirection="column"
              sx={{
                backgroundColor: "white.main",
                border: "1px solid #D8E0E8",
                borderRadius: "20px",
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 3, sm: 4 },
                pb: 0,
              }}
            >
              <SoftBox
                display="flex"
                alignItems="center" gap={1.5}
                sx={{
                  mb: 3,
                }}
              >
               <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: "skyblue.main",
                  }}
                >
                  <LocationCityOutlinedIcon sx={{ color: "primary.main", fontSize: 22 }} />
               </SoftBox> 
              <SoftTypography
                sx={{
                  fontSize: "24px",
                  color: "#1A1C1C",
                  fontWeight: 700,
                }}
              >
                {id ? "Edit City" : "Add New City"}
              </SoftTypography>
              </SoftBox>

              <SoftBox
                display="flex"
                gap={2}
                sx={{ flexDirection: { xs: "column", sm: "row" }, width: "100%", mb: 2.5 }}
              >
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  sx={{ width: { xs: "100%", sm: "50%" } }}
                >
                  <SoftTypography sx={labelSx}>
                    Country <span style={{ color: "#ea0606" }}>*</span>
                  </SoftTypography>
                  <Autocomplete
                    options={countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        error={Boolean(errors.country)}
                        sx={autocompleteSx}
                      />
                    )}
                  />
                  {errors.country && (
                    <SoftTypography sx={{ fontSize: 12, color: "error.main" }}>
                      {errors.country}
                    </SoftTypography>
                  )}
                </SoftBox>

                <SoftBox
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  sx={{ width: { xs: "100%", sm: "50%" } }}
                >
                  <SoftTypography sx={labelSx}>
                    State / Province <span style={{ color: "#ea0606" }}>*</span>
                  </SoftTypography>
                  {selectedCountry && states.length === 0 ? (
                    <SoftInput
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      type="text"
                      placeholder="Enter state / province"
                      error={Boolean(errors.state)}
                      sx={inputFieldSx}
                    />
                  ) : (
                    <Autocomplete
                      options={states}
                      value={selectedState}
                      onChange={handleStateChange}
                      disabled={!selectedCountry}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select state"
                          error={Boolean(errors.state)}
                          sx={autocompleteSx}
                        />
                      )}
                    />
                  )}
                  {errors.state && (
                    <SoftTypography sx={{ fontSize: 12, color: "error.main" }}>
                      {errors.state}
                    </SoftTypography>
                  )}
                </SoftBox>
              </SoftBox>

              <SoftBox display="flex" flexDirection="column" gap={1} sx={{ width: "100%", mb: 2.5 }}>
                <SoftTypography sx={labelSx}>
                  City Name <span style={{ color: "#ea0606" }}>*</span>
                </SoftTypography>
                <SoftInput
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  placeholder="Enter city name"
                  error={Boolean(errors.name)}
                  sx={inputFieldSx}
                />
                {errors.name && (
                  <SoftTypography sx={{ fontSize: 12, color: "error.main" }}>
                    {errors.name}
                  </SoftTypography>
                )}
              </SoftBox>

              <SoftBox display="flex" flexDirection="column" gap={1} sx={{ width: "100%", mb: 3 }}>
                <SoftTypography sx={labelSx}>Status</SoftTypography>
                <SoftBox display="flex" gap={2} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                  {[
                    { label: "Active", value: true },
                    { label: "Inactive", value: false },
                  ].map((option) => {
                    const selected = form.is_active === option.value;
                    const isActiveOption = option.value === true;

                    let borderColor = "#BCC8CE";
                    let backgroundColor = "#FFFFFF";
                    let textColor = "#3D484E";
                    let radioColor = "#BCC8CE";

                    if (selected) {
                      borderColor = "#006780";
                      backgroundColor = "#0067800D";
                      textColor = "#006780";
                      radioColor = "#006780";
                    } 
                    // else if (selected && !isActiveOption) {
                    //   borderColor = "#3D484E";
                    //   backgroundColor = "#F9F9F9";
                    //   textColor = "#3D484E";
                    //   radioColor = "#3D484E";
                    // }

                    return (
                      <SoftBox
                        key={option.label}
                        onClick={() => setForm((prev) => ({ ...prev, is_active: option.value }))}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1.5}
                        sx={{
                          flex: 1,
                          cursor: "pointer",
                          borderRadius: "12px",
                          border: `1.5px solid ${borderColor}`,
                          backgroundColor,
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <SoftBox
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: `2px solid ${radioColor}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                          }}
                        >
                          {selected && (
                            <SoftBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: radioColor,
                                boxSizing: "border-box",
                              }}
                            />
                          )}
                          {!selected && (
                            <SoftBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: `2px solid ${radioColor}`,
                                backgroundColor: "#FFF",
                                boxSizing: "border-box",
                              }}
                            />
                          )}
                        </SoftBox>
                        <SoftTypography
                          fontWeight="medium"
                          sx={{
                            fontSize: 14,
                            color: textColor,
                          }}
                        >
                          {option.label}
                        </SoftTypography>
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              </SoftBox>

              <SoftBox
                display="flex"
                justifyContent="center"
                sx={{
                  mx: { xs: -2, sm: -3, md: -4 },
                  px: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <SoftBox sx={{ width: "90%", borderTop: "1px solid #D8E0E8" }} />
              </SoftBox>

              <SoftBox
                sx={{
                  px: 0,
                  py: 2.5,
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  sx={{ flexDirection: { xs: "column", sm: "row" } }}
                >
                  <SoftButton
                    component={Link}
                    to={backPath}
                    color="dark"
                    variant="outlined"
                    sx={{
                      textTransform: "capitalize",
                      fontSize: 15,
                      fontWeight: 400,
                      width: { xs: "100%", sm: "180px" },
                      borderColor: "#BCC8CE",
                      color: "#3D484E",
                    }}
                  >
                    Cancel
                  </SoftButton>

                  <SoftButton
                    onClick={handleSubmit}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontSize: 15,
                      fontWeight: 400,
                      width: { xs: "100%", sm: "200px" },
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {loading ? (
                      "Loading..."
                    ) : id ? (
                      "Update City"
                    ) : (
                      <>
                        <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 400 }}>+</span>
                        <span style={{ fontWeight: 400 }}>Create City</span>
                      </>
                    )}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        )}
      </SoftBox>
    </DashboardLayout>
  );
}

export default AddCity;
